<template>
  <div class="card border-0 shadow-sm m-4">
    <div class="card-body p-0 gap-4 d-flex flex-column">
      <div class="transaction-list-section p-0">
        <div class="transaction-list-table">
          <div
            class="transaction-list-table-header d-flex align-items-center w-100 flex-wrap px-4 pt-4"
            style="gap: 8px"
          >
            <div class="flex-grow-1">
              <div class="row g-2 align-items-center">
                <div class="col-auto">
                  <div class="input-wrapper search-input-wrapper">
                    <span class="input-icon icon-left">
                      <i class="fa fa-search"></i>
                    </span>
                    <input
                      type="text"
                      placeholder="Search Ref ID"
                      v-model="search"
                      class="search-input styled-input"
                    />
                  </div>
                </div>
                <div class="col-auto">
                  <div class="input-wrapper status-input-wrapper">
                    <select v-model="status" class="status-select styled-input">
                      <option value="" selected>Status</option>
                      <option
                        v-for="option in statusOptions"
                        :key="option.value"
                        :value="option.value"
                      >
                        {{ option.text }}
                      </option>
                    </select>
                    <span class="input-icon icon-right select-arrow">
                      <i class="fa fa-chevron-down"></i>
                    </span>
                  </div>
                </div>
                <div class="col-auto">
                  <SwiftDatePicker
                    v-model="dateRange"
                    placeholder="Select Date Range"
                  />
                </div>
              </div>
            </div>
            <div
              class="d-flex align-items-center ms-auto pe-0 export-btn-responsive"
              style="min-width: 0"
            >
              <argon-button variant="export" @click="exportData()">
                Export
              </argon-button>
            </div>
          </div>
          <hr />
          <div class="mt-4 table-wrapper" style="overflow-x: auto">
            <table class="table">
              <thead>
                <tr>
                  <th
                    scope="col"
                    v-for="(header, index) in tableHeaders"
                    :key="index"
                    :style="getHeaderStyle(header)"
                    :class="[
                      'table-header',
                      header.fixed === 'left' ? 'sticky-col-left' : '',
                      header.fixed === 'right' ? 'sticky-col-right' : '',
                      isSecondToLastRight(index) ? 'sticky-col-right-2' : '',
                    ]"
                  >
                    {{ header.text }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, rowIndex) in filteredTransactions"
                  :key="rowIndex"
                >
                  <td
                    v-for="(header, colIndex) in tableHeaders"
                    :key="colIndex"
                    :style="getHeaderStyle(header)"
                    :class="[
                      'table-body',
                      header.fixed === 'left' ? 'sticky-col-left' : '',
                      header.fixed === 'right' ? 'sticky-col-right' : '',
                      isSecondToLastRight(colIndex) ? 'sticky-col-right-2' : '',
                    ]"
                    style="vertical-align: middle"
                  >
                    <!-- Conditional logic for 'Receipt' column -->
                    <template v-if="header.value === 'receipt'">
                      <button
                        v-if="row.status != 'Failed'"
                        class="download-btn mb-0"
                        @click="downloadReceipt(row.uuid)"
                      >
                        Download
                      </button>
                    </template>
                    <template v-else-if="header.value === 'amount'">
                      IDR {{ formatNumber(row[header.value]) }}
                    </template>
                    <template v-else-if="header.value === 'id'">
                      <div style="color: blue">{{ row[header.value] }}</div>
                    </template>
                    <template v-else-if="header.value === 'status'">
                      <span
                        :class="[
                          'status-badge',
                          statusClass(row[header.value]),
                        ]"
                      >
                        {{ row[header.value] }}
                      </span>
                    </template>
                    <template v-else-if="header.value === 'account_number'">
                      {{ maskAccountNumber(row[header.value], row.bank) }}
                    </template>
                    <template v-else>
                      {{ row[header.value] }}
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            class="d-flex justify-content-between align-items-center p-4"
            style="border-top: 1px solid #eaecf0"
          >
            <div class="d-flex gap-2 align-items-center">
              <div>show</div>
              <div
                class="select-with-icon position-relative"
                style="position: relative"
              >
                <select class="form-control">
                  <option selected>10</option>
                  <option>25</option>
                  <option>50</option>
                  <option>100</option>
                  <option>500</option>
                </select>
                <img
                  src="../../../public/assets/icons/ic_dropdown.svg"
                  alt="icon"
                  class="select-icon"
                />
              </div>
              <div>of</div>
              <div>5</div>
              <div>items</div>
            </div>
            <div class="d-flex gap-2 align-items-center">
              <button class="btn">
                <img
                  src="../../../public/assets/icons/ic_left_arrow.svg"
                  alt="icon"
                  class="arrow left"
                />
              </button>
              <button class="btn">5</button>
              <button class="btn">
                <img
                  src="../../../public/assets/icons/ic_right_arrow.svg"
                  alt="icon"
                  class="arrow right"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <TransactionReceipt
      ref="receiptModal"
      :transaction-id="selectedTransactionId"
      @pdf-generating="handlePdfGenerating"
      @pdf-downloaded="handlePdfDownloaded"
    />
  </div>
  <Loading :isLoading="isLoading" />
</template>

<script>
// import SwiftDataTable from "@/components/SwiftDataTable.vue"; // Ensure correct path
import ArgonButton from "@/components/ArgonButton.vue"; // Ensure correct path
import { detailTransaction } from "@/api/transaction";
import jsPDF from "jspdf";
import { formatNumber as formatNumberUtil, maskAccountNumber } from "@/utils/param-utils";
import Loading from "@/components/Loading";

export default {
  name: "TransactionListComponent",
  components: {
    // SwiftDataTable,
    ArgonButton,
    Loading,
  },
  props: {
    transactions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      dateRange: null,
      status: "",
      search: "",
      perPage: 20,
      currentPage: 1,
      tableHeaders: [
        {
          text: "Transaction ID",
          value: "id",
          fixed: "left",
          sortable: false,
          width: 160,
        }, // Adjust width as needed
        {
          text: "Date&Time",
          value: "transaction_datetime",
          sortable: false,
          width: 160,
        }, // Changed label from Date & Time
        { text: "Amount", value: "amount", sortable: false, width: 200 },
        { text: "Bank", value: "bank", sortable: false, width: 160 },
        {
          text: "Account Number",
          value: "account_number",
          sortable: false,
          width: 200,
        }, // Adjust width
        {
          text: "Account Name",
          value: "account_name",
          sortable: false,
          width: 200,
        },
        {
          text: "Status",
          value: "status",
          sortable: false,
          width: 100,
          fixed: "right",
        }, // Fixed Right? Width needed for offset calc if using CSS
        {
          text: "Receipt",
          value: "receipt",
          sortable: false,
          fixed: "right",
          width: 120,
        }, // Changed text & value, Fixed Right? Width needed
      ],
      statusOptions: [
        { text: "All Status", value: "All Status" },
        { text: "Draft", value: "Refund" },
        { text: "Cancel", value: "Cancel" },
        { text: "Waiting For Payment", value: "Waiting For Payment" },
        { text: "Processing", value: "Processing" },
        { text: "Partial Success", value: "Partial Success" },
        { text: "Success", value: "Success" },
        { text: "Failed", value: "Failed" },
      ],
    };
  },
  computed: {
    filteredTransactions() {
      let filtered = this.transactions;

      if (this.searchBatchId) {
        filtered = filtered.filter((t) =>
          t.id.toLowerCase().includes(this.searchBatchId.toLowerCase())
        );
      }

      // Filter by status if set
      if (this.status) {
        filtered = filtered.filter((item) => item.status === this.status);
      }

      // Filter by date range if set
      if (
        this.dateRange &&
        Array.isArray(this.dateRange) &&
        this.dateRange.length === 2 &&
        this.dateRange[0] &&
        this.dateRange[1]
      ) {
        // Always convert to Date objects
        let startDateObj = this.dateRange[0];
        let endDateObj = this.dateRange[1];
        if (!(startDateObj instanceof Date))
          startDateObj = new Date(startDateObj);
        if (!(endDateObj instanceof Date)) endDateObj = new Date(endDateObj);
        //endDateObj.setHours(23, 59, 59, 999);
        filtered = filtered.filter((item) => {
          let itemDate = item.datetime.split(" ")[0];
          let parts = itemDate.split("/"); // ['14', '01', '2025']
          itemDate = new Date(parts[2], parts[1] - 1, parts[0]); // new Date(year, monthIndex, day)

          if (isNaN(itemDate.getTime())) {
            console.warn("Invalid item.datetime:", item.datetime);
            return false;
          }
          if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
            console.warn(
              "Invalid date range:",
              this.dateRange[0],
              this.dateRange[1]
            );
            return true; // Don't filter if range is invalid
          }

          const strippedItemDate = this.stripTime(itemDate);
          const strippedStart = this.stripTime(startDateObj);
          const strippedEnd = this.stripTime(endDateObj);
          return strippedItemDate >= strippedStart && strippedEnd;
        });
      }

      return filtered;
    },
    totalItems() {
      return this.filteredTransactions.length;
    },
  },
  methods: {
    statusClass(status) {
      switch (status.toLowerCase()) {
        case "success":
          return "success";
        case "failed":
          return "failed";
        case "draft":
          return "draft";
        case "waiting for payment":
          return "waiting";
        case "cancelled":
          return "cancelled";
        default:
          return "";
      }
    },
    async downloadReceipt(transactionId) {
      try {
        this.isLoading = true;
        const response = await detailTransaction(transactionId);
        if (response.status !== "success") {
          throw new Error("Failed to fetch receipt");
        }
        const transactionData = response.data;
        this.generatePdf(transactionData);
        this.isLoading = false;
      } catch (error) {
        this.$swal("Failed to download receipt. Please try again.");
      }
    },
    getHeaderStyle(header) {
      return {
        minWidth: header.width + "px",
        maxWidth: header.width + "px",
      };
    },
    isSecondToLastRight(index) {
      const rightFixedIndexes = this.tableHeaders
        .map((h, i) => (h.fixed === "right" ? i : null))
        .filter((i) => i !== null);

      if (rightFixedIndexes.length === 0) return false;

      const rightMostIndex = rightFixedIndexes[rightFixedIndexes.length - 1];
      return index === rightMostIndex - 1;
    },
    generatePdf(data) {
      const doc = new jsPDF();

      const { brandLogo, brandName } = require("@/utils/branding").useBranding();
      
      doc.setProperties({
        title: "Transfer Receipt",
        creator: brandName,
      });

      const logoUrl = brandLogo;
      const logoWidth = 30;
      const logoHeight = 10;
      doc.addImage(logoUrl, "PNG", 170, 15, logoWidth, logoHeight);

      doc.setFontSize(14);
      doc.text("Receipt", 20, 25);

      doc.setFontSize(24);
      doc.setFont("IBM Plex Sans Devanagari", "bold");
      doc.text("Transfer Successful", 20, 45);
      doc.setFont("IBM Plex Sans Devanagari", "normal");

      // Draw sender information section
      doc.setDrawColor(220, 220, 220);
      doc.roundedRect(20, 55, 170, 40, 3, 3, "S");

      // Add sender information title
      doc.setFontSize(12);
      doc.setTextColor("#128050");
      doc.text("SENDER INFORMATION", 30, 65);

      // Add sender details
      doc.setTextColor("#000000");
      doc.setFontSize(11);
      doc.text("Company Name", 30, 75);
      doc.text(
        localStorage.getItem("merchantName") || "[Company Name Not Available]",
        30,
        85
      );

      doc.text("Email", 120, 75);
      doc.text(
        localStorage.getItem("merchantEmail") ||
          localStorage.getItem("merchant_email") ||
          "[Email Not Available]",
        120,
        85
      );

      // Draw recipient information section
      doc.setFillColor(244, 246, 246);
      doc.roundedRect(20, 105, 170, 40, 3, 3, "FD");

      // Add recipient information title
      doc.setTextColor("#128050");
      doc.setFontSize(12);
      doc.text("RECIPIENT INFORMATION", 30, 115);

      // Add recipient details
      doc.setTextColor("#000000");
      doc.setFontSize(11);
      doc.setFont("IBM Plex Sans Devanagari", "bold");
      doc.text(data.account_name || "-", 30, 125);
      doc.setFont("IBM Plex Sans Devanagari", "normal");
      doc.text(
        data.bank + " - " + maskAccountNumber(data.account_number, data.bank) ||
          "Bank Central Asia - 151515151515",
        30,
        135
      );

      // Add transfer details title
      doc.setTextColor("#128050");
      doc.setFontSize(12);
      doc.text("TRANSFER DETAILS", 20, 160);

      // Reset text color
      doc.setTextColor("#000000");
      doc.setFontSize(11);

      const startY = 170;
      const lineHeight = 10;
      const leftColumnX = 30;
      const rightColumnX = 90;

      const details = [
        ["Transaction ID", data.transaction_id || ""],
        ["Date & Time", data.transaction_datetime || ""],
        ["Amount", "IDR " + formatNumberUtil(data.amount || 0)],
        ["Admin Fee", "IDR " + formatNumberUtil(data.admin_fee || 0)],
        [
          "Total",
          "IDR " + formatNumberUtil((data.amount || 0) + (data.admin_fee || 0)),
        ],
      ];

      // doc.setTextAlign("left");

      for (let i = 0; i < details.length; i++) {
        doc.text(details[i][0], leftColumnX, startY + i * lineHeight, {
          align: "left",
        });
        doc.text(details[i][1], rightColumnX, startY + i * lineHeight, {
          align: "left",
        });

        if (i < details.length - 1) {
          doc.setDrawColor(220, 220, 220);
          doc.line(
            20,
            startY + i * lineHeight + 5,
            140,
            startY + i * lineHeight + 5
          );
        }
      }

      doc.setFillColor(244, 246, 246);
      doc.rect(0, 287, 210, 10, "F");
      doc.setFontSize(12);
      doc.setTextColor("#6c757d");
      doc.text(
        "© PT Anugrah Multi Remittance 2025. All rights reserved.",
        105,
        293,
        { align: "center" }
      );

      doc.save(`Transaction_${data.id || "TRB0011404005"}_Receipt.pdf`);
    },
    exportData() {
      this.loading = true;

      let csv =
        "Transaction ID|Date&Time|Amount|Bank|AccountNumber|AccountName|Status" +
        "\n";

      this.transactions.forEach((row) => {
        csv += row.id + "|";
        csv += row.transaction_datetime + "|";
        csv += row.amount + "|";
        csv += row.bank + "|";
        csv += maskAccountNumber(row.account_number, row.bank) + "|";
        csv += row.account_name + "|";
        csv += row.status;
        csv += "\n";
      });

      const anchor = document.createElement("a");
      anchor.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
      anchor.target = "_blank";
      anchor.download = "DetailTransactionBatch .csv";
      anchor.click();
    },
    formatNumber(value) {
      return formatNumberUtil(value);
    },
    maskAccountNumber(accountNumber, bank) {
      return maskAccountNumber(accountNumber, bank);
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  overflow-x: scroll;
  width: 100%;
  min-height: 330px;
}

.download-btn {
  display: flex;
  padding: 4px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;

  border-radius: 4px;
  border: 1px solid var(--Background-Grey, #dae0e2);
}

table {
  border-collapse: separate;
  border-spacing: 0;
  min-width: 1000px; /* Ensure it overflows to test stickiness */
}

.table-header {
  padding: 13px 8px;
  height: 40px;
  min-width: 100px;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
  background-color: #fcfcfd;
}

.table-body {
  padding: 10px 8px;
  height: 40px;
  min-width: 100px;
  border-top: 1px solid #eaecf0;
  border-bottom: 1px solid #eaecf0;
  background-color: #ffffff;
}

.sticky-col-left {
  position: sticky;
  left: 0;
  z-index: 2;
  box-shadow: 4px 0 6px -2px rgba(0, 0, 0, 0.1); /* Shadow to the right */
}

.sticky-col-right {
  position: sticky;
  right: 0;
  z-index: 2;
}

.sticky-col-right-2 {
  position: sticky;
  right: 120px; /* Width of your last right-fixed column */
  z-index: 1;
  box-shadow: -4px 0 6px -2px rgba(0, 0, 0, 0.08); /* Light inner-left shadow */
}

thead .sticky-col {
  z-index: 2; /* Higher z-index to appear above body rows */
  background-color: #fcfcfd;
}

.select-with-icon {
  position: relative;
  width: 80px;
}

.select-with-icon .select-icon {
  position: absolute;
  right: 12px;
  bottom: 14px;
  width: 12px;
  height: 12px;
  pointer-events: none; /* prevents icon from blocking click on select */
}

@media (max-width: 768px) {
  .transaction-list-table-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .export-btn-responsive {
    width: 100% !important;
    margin-top: 12px;
    justify-content: flex-end !important;
  }

  .export-btn-responsive > .btn {
    width: 100%;
  }
}

/* Keep styles for the overall layout and other elements */
.transaction-list-table {
  width: 100%;
}

.transaction-list-table-header {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
}

/* Shared wrapper style (if not fully encapsulated in BaseDateRangePicker) */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

/* Base styling for inputs/select (used by search and status) */
.styled-input {
  height: 40px;
  border-radius: 100px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #f8fafc !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px !important;
  color: #4a5568;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s ease-in-out;
}

.styled-input:focus {
  border-color: #4299e1 !important;
  outline: none;
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.styled-input::placeholder {
  color: #a0aec0;
  opacity: 1;
}

/* Search Input */
.search-input-wrapper {
  flex-basis: 250px;
}

.search-input.styled-input {
  padding-left: 40px !important;
}

/* Status Select */
.status-input-wrapper {
  min-width: 150px;
}

.status-select.styled-input {
  padding-left: 12px !important;
  padding-right: 35px !important;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  cursor: pointer;
}

.status-select option[disabled] {
  color: #a0aec0;
}

/* Icon positioning (shared) */
.input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.icon-left {
  left: 12px;
}

.icon-right {
  /* Still used by select */
  right: 12px;
}

.select-arrow {
  /* Still used by select */
  pointer-events: none;
}

/* Export Button */
.export-btn {
  margin-left: auto;
  height: 40px;
  display: inline-flex;
  align-items: center;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  display: inline-block;
  text-align: center;
}

.status-badge.success {
  background-color: #dcfce7;
  color: #15803d;
}

.status-badge.failed {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-badge.draft {
  background-color: #f3f4f6;
  color: #4b5563;
}

.status-badge.waiting {
  background-color: #fff7ed;
  color: #ea580c;
}

.status-badge.cancelled {
  background-color: #f3f4f6;
  color: #6b7280;
}

.download-btn {
  background-color: white;
  color: black;
  border: 1px solid #dae0e2;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;
  max-width: 90px;
  white-space: nowrap;
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #f8f9fa; /* Light gray background */
  text-align: center;
  padding: 10px 0;
  font-size: 14px;
  color: #6c757d; /* Muted text color */
  box-shadow: 0 -1px 5px rgba(0, 0, 0, 0.1);
}
</style>
